import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MessageCircle } from "lucide-react";
import { useLanguage, waLink } from "../i18n";
import PaymentBadges from "./PaymentBadges";

const EASE = [0.22, 1, 0.36, 1];

const Faq = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(0);

  return (
    <section className="py-24 sm:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
          >
            {t.faq.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
            className="font-serif text-4xl font-black tracking-tight text-[#0D1B2A] sm:text-5xl"
          >
            {t.faq.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
            className="mx-auto mt-4 max-w-xl text-base text-[#0D1B2A]/60"
          >
            {t.faq.sub}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="overflow-hidden rounded-3xl border border-[#E6DEC8] bg-white"
        >
          {t.faq.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                data-testid={`faq-item-${i + 1}`}
                className={i > 0 ? "border-t border-[#E6DEC8]" : ""}
              >
                <button
                  data-testid={`faq-question-${i + 1}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 px-7 py-5 text-left transition-colors duration-300 hover:bg-[#FAF6F0]"
                >
                  <span className="font-serif text-lg font-bold text-[#0D1B2A] sm:text-xl">
                    {item.q}
                  </span>
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                      isOpen ? "rotate-180 bg-[#C85A32] text-white" : "bg-[#F2E8D5] text-[#0D1B2A]"
                    }`}
                  >
                    <ChevronDown size={16} strokeWidth={2.5} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="px-7 pb-6">
                        <p className="text-sm leading-relaxed text-[#0D1B2A]/65 sm:text-base">
                          {item.a}
                        </p>
                        {item.pay && <PaymentBadges testid="faq-payment-icons" />}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
          className="mt-10 flex justify-center"
        >
          <a
            data-testid="faq-whatsapp-cta"
            href={waLink(t.waGeneric)}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-[#25D366]/30 transition-all duration-300 hover:scale-[1.04]"
          >
            <MessageCircle size={18} strokeWidth={2.4} className="transition-transform duration-300 group-hover:-rotate-12" />
            {t.faq.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;
